{
  "bulkOnboarding": {
    "departments": {
      "title": "Create departments",
      "description": "Upload an Excel file with your organization's department structure. Departments will be stored temporarily until managers are assigned in the next steps.",
      "uploadTitle": "Upload Departments from Excel",
      "toast": {
        "createSuccess": "Departments created successfully!",
        "createFailed": "Failed to create departments",
        "validationRequired": "Please upload departments first",
        "fixErrors": "Please fix validation errors before submitting"
      }
    },
    "users": {
      "title": "Create employees",
      "description": "Upload an Excel file with your organization's employees. When employees are created, departments with matching manager emails will be automatically assigned. Any remaining departments will need manual resolution in the next step.",
      "uploadTitle": "Upload Employees from Excel",
      "toast": {
        "createSuccess": "Employees created successfully! Departments with matching manager emails have been automatically assigned.",
        "createFailed": "Failed to create employees",
        "validationRequired": "Please upload employees first",
        "fixErrors": "Please fix validation errors before submitting"
      }
    },
    "processes": {
      "uploadTitle": "Upload Processes from Excel",
      "toast": {
        "createSuccess": "Processes created successfully! Process owners have been automatically assigned based on department managers.",
        "createFailed": "Failed to create processes",
        "fixErrors": "Please fix validation errors before submitting"
      }
    },
    "resolution": {
      "description": "In some cases we cannot unambiguously link the manager email to an employee. Review your results.",
      "loading": "Loading temporary departments...",
      "instructions": "Select a manager for each department to migrate it from temporary to permanent status.",
      "table": {
        "name": "Name",
        "abbreviation": "Abbreviation",
        "managerEmail": "Searched email",
        "managerFound": "Manager found and ready to resolve",
        "alreadyResolved": "Already resolved",
        "noDescription": "No description",
        "assignManager": "Assigned manager"
      },
      "toast": {
        "batchSuccess": "Successfully resolved {{count}} department(s)",
        "partialSuccess": "Resolved {{success}} department(s). {{failed}} failed.",
        "batchFailed": "Failed to resolve departments",
        "unresolvedDepartments": "Please resolve all departments before completing setup. Each department must have a manager assigned."
      },
      "success": {
        "title": "All departments resolved!",
        "description": "All departments have been successfully assigned managers."
      }
    },
    "assignManager": {
      "selectPlaceholder": "Select employee…"
    },
    "validation": {
      "nameRequired": "Name is required",
      "abbreviationRequired": "Abbreviation is required",
      "descriptionRequired": "Description is required",
      "invalidEmail": "Invalid email address",
      "firstNameRequired": "First name is required",
      "lastNameRequired": "Last name is required",
      "departmentRequired": "Department is required",
      "roleRequired": "Position is required",
      "ownerEmailInvalid": "A valid email address is required",
      "orgFunctionNameRequired": "Function / Team is required",
      "licenceSeatsInvalid": "Licence seats must be a whole number of 0 or more",
      "unknownMemberEmail": "{{email}} does not match any member",
      "duplicateEntry": "Duplicate entry",
      "circularParent": "A department cannot be its own parent — not even through intermediate levels",
      "existingMember": "Already exists — this member is already part of your organization and cannot be edited here",
      "roleDescriptionConflict": "Same position, different descriptions — a position has exactly one description",
      "accountableEmailInvalid": "not a valid email address",
      "accountableEmailNotFound": "the email {{email}} does not match any member"
    },
    "excel": {
      "sheets": {
        "instructions": "Instructions",
        "departments": "Departments",
        "users": "Employees",
        "processes": "Processes",
        "functions": "Functions",
        "teams": "Teams",
        "memberships": "Memberships",
        "systems": "Systems",
        "dataSyncPeople": "Employees"
      },
      "columns": {
        "departments": {
          "name": "Name*",
          "abbreviation": "Abbreviation*",
          "description": "Description",
          "parentAbbreviation": "Parent Abbreviation",
          "managerEmail": "Manager Email"
        },
        "users": {
          "firstName": "First Name*",
          "lastName": "Last Name*",
          "email": "Email*",
          "department": "Department*",
          "role": "Position*",
          "roleDescription": "Position Description"
        },
        "processes": {
          "name": "Name*",
          "description": "Description*",
          "department": "Department*",
          "ownerEmail": "Process Owner Email*",
          "accountableEmail": "Accountable Email (optional)"
        },
        "functions": {
          "name": "Name*",
          "description": "Description",
          "department": "Department",
          "leadEmail": "Lead Email",
          "email": "Shared Mailbox Email"
        },
        "teams": {
          "name": "Name*",
          "description": "Description",
          "department": "Department",
          "leadEmail": "Lead Email",
          "email": "Shared Mailbox Email"
        },
        "memberships": {
          "orgFunctionName": "Function / Team*",
          "memberEmail": "Member Email*"
        },
        "systems": {
          "name": "Name*",
          "description": "Description",
          "category": "Category",
          "url": "URL",
          "licenceSeats": "Licence Seats",
          "ownerEmail": "System Owner Email*"
        },
        "dataSync": {
          "id": "ID",
          "parentDepartment": "Parent Department",
          "lead": "Lead Email",
          "owner": "System Owner Email",
          "location": "Location"
        }
      }
    }
  },
  "welcomePage": {
    "welcome": "Welcome",
    "checkTask": "Check your tasks",
    "minimizeVolume": "Minimal effort for you, maximum benefit for all",
    "minimizeVolumeDesc": "Record and optimize processes with minimal effort and maximum efficiency.",
    "allResourceInOne": "All process resources in one place",
    "allResourceInOneDesc": "Manage all process-relevant resources centrally and efficiently.",
    "completeProcess": "Complete process overview - Easy to understand",
    "completeProcessDesc": "Keep track of all processes with our intuitive BPMN representation.",
    "howRecordingWork": "How does viral process recording work?",
    "continueToDashboard": "Continue to Dashboard"
  },
  "initialSetup": {
    "title": "Initial setup",
    "wizardHeading": "BLIKS IO Setup Wizard",
    "topBar": {
      "logoAlt": "BLIKS IO logo"
    },
    "pending": {
      "title": "Initial setup not yet completed",
      "description": "Your organization hasn't been fully set up yet. An Organization Manager in your organization needs to complete the initial setup first – once that's done, you can get started right away. This page refreshes automatically."
    },
    "removalBlocked": {
      "department": {
        "processes": "The department cannot be deleted: {{count}} processes reference it. Reassign the processes to another department in the Processes step first."
      },
      "user": {
        "systems": "The employee cannot be deleted: {{count}} systems have them as owner. Reassign the systems to another person in the Systems step first.",
        "processes": "The employee cannot be deleted: {{count}} processes have them as owner or creator. Reassign the processes to another person in the Processes step first."
      },
      "orgFunction": {
        "mainRole": "The function cannot be deleted: it is the main function of {{count}} employees. Change their position in the Employees step first."
      }
    },
    "completeBlocked": {
      "title": "The initial setup cannot be finished yet:",
      "unresolvedDepartments": "{{count}} departments have no assigned manager.",
      "goToResolve": "Assign managers",
      "usersWithoutDepartment": "{{count}} employees have no department.",
      "goToUsers": "Go to the Employees step"
    },
    "steps": {
      "welcome": "Welcome",
      "departments": "Departments",
      "employees": "Employees",
      "resolveManagers": "Resolve managers",
      "functions": "Functions",
      "teams": "Teams",
      "systems": "Systems",
      "processes": "Processes",
      "overview": "Overview & finish"
    },
    "stepDescriptions": {
      "departments": "The framework of your organization",
      "employees": "People, each assigned to one position",
      "resolveManagers": "Managers are linked from the employees you added",
      "functions": "Additional functions employees hold",
      "teams": "Teams in which employees work together",
      "systems": "The software systems in use",
      "processes": "A list for later viral capture",
      "overview": "Org chart preview & confirmation"
    },
    "footer": {
      "next": "Next",
      "skip": "Skip",
      "finish": "Finish",
      "start": "Let's go"
    },
    "autosave": {
      "pending": "Unsaved changes",
      "saving": "Saving…",
      "saved": "Saved automatically",
      "error": "Autosave failed — Next will retry"
    },
    "welcome": {
      "title": "A warm welcome to BLIKS IO",
      "intro": "Before BLIKS IO is rolled out in your organization, we'll build a complete picture of your organization together in just a few steps. Don't worry: nobody receives a notification during setup — not even the people you create here.",
      "checklist": {
        "title": "What you should have ready",
        "departments": "Your organizational departments and their hierarchy",
        "people": "All people in your organization",
        "systems": "The systems in use",
        "processes": "The processes you want to capture later"
      },
      "pauseResume": "This information usually lives centrally in your HCM and EAM software. Something missing? No problem — you can pause at any time and pick up exactly where you left off."
    },
    "outcomes": {
      "title": "What you get at the end",
      "description": "After the initial setup, BLIKS IO is ready to be rolled out across your organization. The four building blocks produce three clear results.",
      "cards": {
        "structure": {
          "title": "Organizational structure",
          "body": "Departments, positions and teams & additional functions. The org chart emerges automatically from the relationships."
        },
        "systems": {
          "title": "System overview",
          "body": "All systems in use in your organization — including catalog metadata."
        },
        "processes": {
          "title": "Process overview",
          "body": "The processes you want to capture — how they really run is recorded later by BLIKS IO through viral capture."
        }
      },
      "hint": "<bold>Two things up front:</bold> It's fine if not everything is complete in the initial setup — you can always refine things manually later. And would you like to build and keep your organization up to date automatically? Talk to us: we connect BLIKS IO directly to your HCM and EAM software."
    },
    "doneNotice": {
      "departments": {
        "title": "Departments are already set up",
        "description": "The import is complete. <bold>Next</bold> saves edits to existing rows and new rows directly. Removing a row deletes the imported entry and clears references to it — any resulting gaps must be fixed before finishing. You can also change a department's manager here via the Manager Email column."
      },
      "users": {
        "title": "Employees are already set up",
        "description": "The import is complete. <bold>Next</bold> saves edits to existing rows and new rows directly. Removing a row deletes the imported entry and clears references to it — any resulting gaps must be fixed before finishing."
      },
      "optional": {
        "title": "This step has already been completed",
        "description": "You can add more entries at any time – existing ones remain unchanged."
      }
    },
    "resolve": {
      "counters": {
        "open": "Open",
        "resolved": "Already resolved"
      },
      "suggestion": "Suggestion: {{name}}",
      "applySuggestion": "Apply"
    },
    "functionsTeams": {
      "autoCreatedBanner": "{{count}} functions were already created from your employees' position titles.",
      "functionsSection": {
        "title": "Functions",
        "stepTitle": "Create functions",
        "description": "Employees often act not as a person but in a function — e.g. “data protection officer” or “product owner”. Create them here or upload them via Excel.",
        "hint": "If you don't have the data at hand right now, you can skip this step — functions can be added at any time."
      },
      "teamsSection": {
        "title": "Teams",
        "stepTitle": "Create teams",
        "description": "Groups that work together — e.g. “First Level Support Team”. Create teams and then assign members to them.",
        "hint": "Optional — teams can be added at any time, even during process capture."
      },
      "columns": {
        "lead": "Lead",
        "sharedMailbox": "Shared mailbox",
        "member": "Employee",
        "function": "Function",
        "team": "Team"
      },
      "membershipsTable": {
        "heading": "Memberships"
      },
      "mailImportNote": "During Excel import, the lead is matched via a person's email address. Emails that are not found are highlighted in red and must be assigned to a person manually. You create teams in the next step.",
      "toast": {
        "fixErrors": "Please fix the errors in the uploaded rows first",
        "createSuccess": "{{created}} entries created, {{linked}} memberships linked",
        "createFailed": "The import failed"
      },
      "mainRoleLockedRemove": "This function is assigned as a position and can't be deleted – change the affected members' position first."
    },
    "systems": {
      "title": "Capture systems",
      "description": "Which software does your organization use? Capture each system with the key details — or upload an existing system list via Excel.",
      "toast": {
        "fixErrors": "Please fix the errors in the uploaded rows first",
        "createSuccess": "Systems saved",
        "createFailed": "Systems could not be created"
      }
    },
    "processes": {
      "title": "Capture processes",
      "description": "Which processes should BLIKS IO record? Capture only the top-line list here — employees deliver the details later.",
      "hint": "You can add more processes at any time — or skip this step entirely.",
      "mailImportNote": "Owners are assigned via a person. During Excel import, matching happens via the email — emails that are not found are marked in red as “user not found”.",
      "ownerEmail": "Process owner (email)"
    },
    "columns": {
      "systems": {
        "name": "System name",
        "licenceSeats": "Licenses",
        "owner": "Responsible"
      },
      "processes": {
        "owner": "Process owner",
        "accountable": "Accountable"
      }
    },
    "columnHints": {
      "departments": {
        "name": "Full name of the department",
        "abbreviation": "Short & unique, e.g. \"FC\"",
        "description": "What is the department responsible for?",
        "parentAbbr": "Empty = top level",
        "managerEmail": "Email of the person in charge"
      },
      "users": {
        "firstName": "e.g. \"Patrick\"",
        "lastName": "e.g. \"Lenz\"",
        "email": "Work email, must be unique",
        "department": "Department the person works in",
        "role": "The position, e.g. \"Head of Controlling\"",
        "roleDescription": "Tasks & focus areas of the position"
      },
      "functions": {
        "name": "e.g. \"Data Protection Officer\"",
        "description": "Tasks & responsibilities",
        "department": "Empty if cross-functional",
        "lead": "Email of the lead",
        "sharedMailbox": "Shared mailbox, e.g. support@…"
      },
      "memberships": {
        "member": "Person who performs the function",
        "target": "The person's function or team"
      },
      "systems": {
        "name": "Name of the system or vendor",
        "category": "e.g. CRM, ERP, HR tool",
        "url": "Vendor website",
        "licenceSeats": "Available licences, if limited",
        "owner": "Person responsible for the system"
      },
      "processes": {
        "name": "e.g. \"New customer setup\"",
        "description": "What does the process deliver? Start & end",
        "department": "Department the process belongs to",
        "owner": "Responsible for keeping the documentation current",
        "accountable": "Optional — defaults to the department manager"
      }
    },
    "fieldExplainer": {
      "required": "Required",
      "optional": "Optional",
      "headings": {
        "departments": "This is the information we need per department",
        "users": "This is the information we need per person",
        "functionsTeams": "This is the information we need – split into two areas",
        "systems": "This is the information we need per system",
        "processes": "This is the information we need per process"
      },
      "sections": {
        "entity": "1. Function or team",
        "memberships": "2. Memberships"
      },
      "fields": {
        "departments": {
          "name": "Full name of the department, e.g. \"Controlling & Reporting\"",
          "abbreviation": "Short, unique code, e.g. \"FC\". Serves as the anchor to link departments together.",
          "description": "What is the department responsible for? e.g. \"Planning, budgeting, reporting\". The precision of the description can influence AI-based analysis results. You can expand the description at any time later.",
          "parentAbbr": "The abbreviation of the parent department – leave empty only for the top level. Your org chart is built automatically from this.",
          "managerEmail": "Email of the person who leads the department. Linked automatically in step 2 as soon as the person exists as an employee."
        },
        "users": {
          "firstName": "First name of the person, e.g. \"Patrick\"",
          "lastName": "Last name of the person, e.g. \"Lenz\"",
          "email": "The person's business email address. Must be unique. Matched against manager emails from step 1 and linked automatically.",
          "department": "The abbreviation of the department the person works in – from the list in step 1. Places the person and the position in the org chart.",
          "role": "What is the person employed to do? e.g. \"Head of Controlling\", \"Accounting Clerk\". This is the position.",
          "roleDescription": "What is this position responsible for? Tasks, responsibilities, focus areas. The basis for later analyses – the more precise, the better the results."
        },
        "functions": {
          "name": "Unique name of the function, e.g. \"Data Protection Officer\", or of the team, e.g. \"First Level Support Team\"",
          "description": "Tasks and responsibilities. The basis for later analyses.",
          "department": "Leave empty for cross-functional functions such as \"Product Owner\". Only fill in when functions or teams are clearly assigned to one department.",
          "lead": "Email of the person who leads the function or team. Often only teams (e.g. \"First Level Support Team\") have a lead.",
          "sharedMailbox": "Shared mailbox of the function or team, e.g. \"support@…\""
        },
        "memberships": {
          "member": "Email of a person from step 2. A person can be active in any number of functions and teams.",
          "target": "Name of the function or team from the area above. Links people to functions."
        },
        "systems": {
          "name": "What is the system or its vendor called? If the system is known under a specific name in your organization, feel free to use your internal vocabulary.",
          "description": "Describe the system's capabilities. What do you use it for?",
          "category": "What kind of system is it? e.g. \"CRM\", \"ERP\", \"HR tool\"",
          "url": "At which URL can the system's vendor be found?",
          "licenceSeats": "For some of your systems you only have a limited number of licenses. If a limit exists, you can note the number here. We analyze how far your day-to-day operations use up the license seats.",
          "owner": "Email of the person in your company who is responsible for the system and can best describe what happens in the system and how."
        },
        "processes": {
          "name": "A clear name, e.g. \"New customer setup\", \"Sales process\" or similar.",
          "description": "What does this process deliver? What does it start with, what does it end with? This description is the basis for later analyses and for the recording by the employees – the more precise, the better.",
          "department": "Abbreviation of the department from step 1 the process belongs to.",
          "owner": "Email of the person who owns this process in BLIKS IO. Important: this only means the responsibility for keeping the process documented and up to date in BLIKS IO – not the role responsible for its content or results in the company. If this role is not clearly defined in your organization, choose the person best placed to maintain the documentation.",
          "accountable": "Email of the person accountable for this process (RACI sense). Optional: if left empty, the current department manager is shown as accountable — this also tracks later manager changes automatically."
        }
      }
    },
    "overview": {
      "title": "Congratulations",
      "subtitle": "Your organization is now captured in BLIKS IO.",
      "description": "You have set up the skeleton. The rest — who really does what, how processes actually run, which shadow systems are in use — is added automatically by employees during recording from now on.",
      "cards": {
        "departments": "Departments",
        "members": "Employees & positions",
        "functions": "Functions",
        "teams": "Teams",
        "systems": "Systems",
        "processes": "Processes",
        "departmentLevels": "across {{count}} levels",
        "distinctPositions": "{{count}} position titles",
        "fromPositions": "{{count}} of them from positions",
        "readyForRecording": "ready for recording"
      },
      "organigram": {
        "title": "Your org chart",
        "description": "Built automatically from the department relationships.",
        "fullscreen": "View fullscreen"
      },
      "toast": {
        "completed": "Initial setup completed!",
        "completeFailed": "Completing the setup failed",
        "pendingChanges": "There are changes that have not been applied yet. Please confirm the affected step with \"Next\" before finishing the setup."
      },
      "invite": {
        "checkboxLabel": "Send a login invitation to all {{count}} members now",
        "helper": "Members receive an email to activate their account and set a password.",
        "success": "{{count}} invitations sent",
        "skipped": "{{count}} members skipped"
      }
    },
    "upload": {
      "errorBanner": "Please fix the errors highlighted in the table.",
      "invalidRowsCount": "{{count}} row(s) with validation errors"
    },
    "uploadFromExcel": "Import Excel",
    "personio": {
      "load": "Load from Personio",
      "loaded": "Loaded from Personio.",
      "unlinkDescription": "The credentials are deleted; rows already loaded stay."
    },
    "tables": {
      "departments": "Departments",
      "users": "Employees",
      "systems": "Systems",
      "processes": "Processes"
    },
    "draftTable": {
      "removeRow": "Remove row",
      "noSelection": "– none –",
      "userNotFound": "User not found"
    },
    "existingEntities": {
      "title": "Already existing ({{count}})",
      "editableNote": "These members already exist — name and email are locked. Assign their department and position here."
    },
    "protectedRows": {
      "lockedIdentity": "This member's name and email are managed by their existing account and cannot be changed here.",
      "placementFailed": "Saving the department assignment for existing members failed."
    },
    "addRow": {
      "department": "Add department",
      "employee": "Add employee",
      "orgFunction": "Add function",
      "team": "Add team",
      "membership": "Add membership",
      "system": "Add system",
      "process": "Add process"
    },
    "clearDrafts": "Clear list",
    "clearConfirm": {
      "title": "Clear list?",
      "description": "All rows in the table will be removed. This cannot be undone.",
      "deleteAllDescription": "All rows will be removed. Already imported entries are permanently deleted when you continue.",
      "confirm": "Clear"
    },
    "pendingDeleteAll": "The list was cleared: all imported entries will be permanently deleted when you continue with \"Next\".",
    "doneBadge": "Completed",
    "usersImport": {
      "membersNotLoaded": "The member list hasn't loaded yet — please try again."
    }
  }
}
